import styled from 'styled-components';
import { ImpactCategoryId } from '@upright/common/model/input/impacts/impact-categories';
import { Dimension, Valence } from '../../../../../../../common/model/types';
import { friendlyValence } from '../../../../../../../common/utils';
import { TurningChevronIcon } from '../../../../../generic/components/icons/turning-chevron';
import { BouColor } from '../../../../../generic/global/bou-colors';
import { disableTextSelection } from '../../../../../generic/utilities/fonts';
import { useValuesSelectorContext } from '../context';
import { ValueSetterOfImpactCategory } from '../context/types';
import { ImpactRow } from './impact-row';
import { stepRange, ValueSelectorRadioButton } from './shared';
import { usePushGtmEvent } from '@upright/ui/common/generic/components/google-tag-manager/use-push-gtm-event';
import { CustomGtmEvent } from '@upright/ui/common/generic/components/google-tag-manager/use-push-gtm-event/payloads';

export const ValenceBlock = (props: {
  valence: Valence;
  dimension: Dimension;
  impactCategories: ImpactCategoryId[];
}) => {
  const context = useValuesSelectorContext();
  const stateHooksOfValence = context.stateHooks[props.dimension][props.valence];
  const [value, setValue] = stateHooksOfValence.valueStateHookOfValence;
  const selectionStatesOfImpactCategories = stateHooksOfValence.stateHooksOfImpactCategories;
  const pushGtmEvent = usePushGtmEvent();

  const { impactCategoryValues, impactCategorySetters } = props.impactCategories.reduce(
    (valuesAndSetters, impactCategory) => {
      const [valueOfCategory, valueSetterOfCategory] = selectionStatesOfImpactCategories[impactCategory];
      return {
        impactCategoryValues: [...valuesAndSetters.impactCategoryValues, valueOfCategory],
        impactCategorySetters: [...valuesAndSetters.impactCategorySetters, valueSetterOfCategory],
      };
    },
    { impactCategoryValues: [] as number[], impactCategorySetters: [] as ValueSetterOfImpactCategory[] },
  );

  const [open, setOpen] = stateHooksOfValence.openStateHookOfValence;

  const grayMode = value === undefined;

  return (
    <>
      <ValenceRow open={open}>
        <ValenceTitleCell onClick={() => setOpen(!open)}>
          <TurningChevronIcon open={open} />
          <span>{friendlyValence(props.valence)}</span>
        </ValenceTitleCell>
        {stepRange.map(index => (
          <RadioButtonCell key={index}>
            <ValueSelectorRadioButton
              checked={value === index || impactCategoryValues.includes(index)}
              onSelect={() => {
                setValue(index, true);
                impactCategorySetters.forEach(setter => setter(index, false));
              }}
              onClick={() => {
                pushGtmEvent({
                  event: CustomGtmEvent.VALUE_SET_DIMENSION_TWEAK,
                  data: {
                    dimension: props.dimension,
                    valence: props.valence,
                    weight: index,
                  },
                });
              }}
              color={grayMode ? BouColor.TOOL_TEXT_DISABLED_GRAY : undefined}
              hoverColor={grayMode ? BouColor.TOOL_TEXT_BLACK : undefined}
              grayWhenUnchecked={!grayMode}
            />
          </RadioButtonCell>
        ))}
      </ValenceRow>
      {open &&
        props.impactCategories.map((impactCode, index) => {
          return (
            <ImpactRow
              key={index}
              impactCategory={impactCode}
              dimension={props.dimension}
              valence={props.valence}
              index={index}
            />
          );
        })}
    </>
  );
};

const ValenceRow = styled.tr<{ open: boolean }>`
  cursor: pointer;
  ${disableTextSelection()}
  > td {
    padding-top: 1em;
    padding-bottom: ${props => props.open && '1em'};
  }
`;

const ValenceTitleCell = styled.td`
  display: flex;
  align-items: center;
  * + * {
    margin-left: 1em;
  }
`;
const RadioButtonCell = styled.td``;
